import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { HeroFeedItem as HeroFeedItemType } from '#src/app/pages/psf/promoteOrShare/pageContext';

import { smartTruncateByWidth } from './textMeasurement';

const AVATAR_SIZE = 32;
const CONTAINER_PADDING = 24;

const useContainerWidth = (isDesktopMode: boolean) => {
	const [availableWidth, setAvailableWidth] = useState<number>(0);
	const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);

	const handleSetContainerRef: (element: HTMLElement | null) => void = useCallback((element: HTMLElement | null) => {
		setContainerElement(element);
	}, []);

	useEffect(() => {
		if (isDesktopMode || !containerElement || typeof ResizeObserver === 'undefined') return undefined;

		const measureWidth = () => {
			const containerWidth = containerElement.offsetWidth || 280;
			const textWidth = containerWidth - AVATAR_SIZE - CONTAINER_PADDING;
			setAvailableWidth(textWidth);
		};

		measureWidth();

		const resizeObserver = new ResizeObserver(measureWidth);
		resizeObserver.observe(containerElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [isDesktopMode, containerElement]);

	return { availableWidth, setContainerRef: handleSetContainerRef };
};

const getTranslatedTexts = (
	item: HeroFeedItemType,
	translate: (key: string, replacements: Record<string, string>) => string,
) => {
	if (item.event.__typename === 'HeroFeedPromotionEvent') {
		const metaAltTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.promote_activity_text', {
			promoter_name: item.userDisplayName,
			amount_formatted: item.event.amountLocalizedWithCurrency ?? '',
		});

		const metaTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.promote_activity_text', {
			promoter_name: '',
			amount_formatted: item.event.amountLocalizedWithCurrency ?? '',
		});

		return { metaAltTextHtml, metaTextHtml };
	}

	const metaAltTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.share_activity_text', {
		sharer_name: item.userDisplayName,
	});

	const metaTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.share_activity_text', {
		sharer_name: '',
	});

	return { metaAltTextHtml, metaTextHtml };
};

export function useHeroFeedItem(
	item: HeroFeedItemType,
	isDesktopMode: boolean = false,
): {
	data: {
		altText: string;
		metaText: string;
		displayName: string;
		displayMetaText: string;
	};
	actions: {
		setContainerRef: (element: HTMLElement | null) => void;
	};
} {
	const { translate } = useI18n();
	const { availableWidth, setContainerRef } = useContainerWidth(isDesktopMode);

	const memoResult = useMemo(() => getTranslatedTexts(item, translate), [item, translate]);

	const altText = memoResult.metaAltTextHtml.replace(/<[^>]+>/g, '');
	const metaText = memoResult.metaTextHtml.replace(/<[^>]+>/g, '');

	const { name: displayName, description: displayMetaText } = useMemo<{ name: string; description: string }>(() => {
		if (isDesktopMode || availableWidth === 0) {
			return { name: item.userDisplayName, description: metaText };
		}

		return smartTruncateByWidth(item.userDisplayName, metaText, availableWidth);
	}, [isDesktopMode, item.userDisplayName, metaText, availableWidth]);

	return {
		data: {
			altText,
			metaText,
			displayName,
			displayMetaText,
		},
		actions: {
			setContainerRef,
		},
	};
}
